import React, { useContext, useEffect, useState } from "react";
import clsx from "clsx";
import { CommentCount, DiscussionEmbed } from "disqus-react";
import { useSelector } from "react-redux";
import { Button, createClasses } from "@bloomberg/media-ui-core";
import { useFortress } from "@foundation/fortress";

import { selectCurrentSlug } from "~redux/features/transporter-slice";
import { CurrentStoryContext } from "~utils/currentStoryContext";
import { getFortressState } from "~utils/getFortressState";
import isOpinion from "~utils/isOpinion";

import styles from "./styles.module.css";

declare const window: Window &
    typeof globalThis & {
        DISQUS: any;
        disqus_config: any;
    };

interface DisqusProps {
    className?: string;
}

export default function Disqus({ className = "" }: DisqusProps) {
    const fortressContext = useFortress();
    const { isAllowed } = getFortressState(fortressContext);
    const { story } = useContext(CurrentStoryContext);
    const currentSlug = useSelector(selectCurrentSlug);
    const inView = currentSlug === story.slug;

    const [disqusInitialized, setDisqusInitialized] = useState(false);
    const [commentsLoaded, setCommentsLoaded] = useState(false);

    const shortName = "bloombergview";
    const { id, url, metadata } = story;
    const config = {
        identifier: id,
        url,
    };

    const renderDisqus =
        isAllowed && isOpinion(story) && !metadata?.suppressComments;

    useEffect(() => {
        // hide disqus component on state change,
        // to ensure comments relevant to current article
        setCommentsLoaded(false);

        // reset disqus component on transport
        if (!inView) {
            // reload disqus component for new story via disqus window api
            if (window.DISQUS && typeof window.DISQUS.reset === "function") {
                window.DISQUS.reset({
                    reload: true,
                    config,
                });
            }

            return;
        }

        window.disqus_config = config;
    }, [inView, id]);

    if (!renderDisqus) {
        return null;
    }

    const toggleComments = () => {
        setCommentsLoaded(!commentsLoaded);

        // to load disqus embed only after button click
        if (!disqusInitialized) {
            setDisqusInitialized(true);
        }
    };

    const embedClass = createClasses([
        styles.disqusEmbed,
        {
            [styles.showEmbed]: Boolean(commentsLoaded),
        },
    ]);

    return (
        <div className={clsx(styles.disqus, className)} data-component="disqus">
            <Button className={styles.commentsButton} onClick={toggleComments}>
                <CommentCount shortname={shortName} config={config}>
                    0 Comments
                </CommentCount>
            </Button>

            {disqusInitialized && (
                <div className={embedClass}>
                    <DiscussionEmbed shortname={shortName} config={config} />
                </div>
            )}
        </div>
    );
}
